/**
 * The live match screen — the glue between the authoritative room, the renderer
 * and the interface.
 *
 * Server messages become scene effects and sound here; the coarse room state is
 * mirrored into the React store only when something a human would notice
 * changes, so the HUD is not re-rendered by the 30 Hz tick.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { TIMING } from '@spell/shared-types';
import { C2S } from '@spell/network-protocol';
import { net } from '../networking/net';
import { useNet, selectOpponent } from '../networking/netStore';
import { Hud } from '../ui/Hud';
import { ConnectionCheck } from '../ui/ConnectionCheck';
import { MatchResult } from '../ui/MatchResult';
import { DebugOverlay } from '../debug/DebugOverlay';
import { SpellCraft, type CraftResult } from '../crafting/SpellCraft';
import { useCombatInput } from './useCombatInput';
import { useRoomStateMirror, useServerEvents } from './roomBridge';
import { resetScene } from '../scene/sceneBus';
import { voiceBridge } from '../networking/webrtc';
import { useSettings } from './settingsStore';

export interface MatchScreenProps {
  onLeave: () => void;
}

export function MatchScreen({ onLeave }: MatchScreenProps) {
  const phase = useNet((s) => s.phase);
  const round = useNet((s) => s.round);
  const craft = useNet((s) => s.craft);
  const fatalError = useNet((s) => s.fatalError);
  const abortReason = useNet((s) => s.abortReason);
  const showDebug = useSettings((s) => s.showDebug);
  const [craftBusy, setCraftBusy] = useState(false);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [locked, setLocked] = useState(false);

  const combatActive = phase === 'round' || phase === 'countdown';
  const input = useCombatInput(canvas, combatActive);
  const inputRef = useRef(input);
  inputRef.current = input;

  /* --- bind to the shared canvas -------------------------------------- */
  useEffect(() => {
    const found = document.getElementById('spell-canvas') as HTMLCanvasElement | null;
    setCanvas(found);
  }, []);

  /* --- scene mode ------------------------------------------------------ */
  useEffect(() => {
    resetScene('match');
    return () => resetScene('idle');
  }, []);

  /* --- pointer lock ---------------------------------------------------- */
  /**
   * Pointer lock changes outside React, so the click-to-play surface has to be
   * driven by a subscription rather than by reading document.pointerLockElement
   * during render: read that way it is sampled once, the lock is then granted
   * without anything telling React about it, and the overlay stays on screen —
   * over the round it was supposed to hand the player into — until some
   * unrelated state change happens to re-render the screen.
   */
  useEffect(() => {
    const onChange = () => setLocked(document.pointerLockElement !== null);
    onChange();
    document.addEventListener('pointerlockchange', onChange);
    return () => document.removeEventListener('pointerlockchange', onChange);
  }, []);

  /* --- room -> interface ------------------------------------------------ */
  useRoomStateMirror();
  useServerEvents({ onSpellGranted: useCallback(() => setCraftBusy(false), []) });

  /* --- craft submit ---------------------------------------------------- */
  const submitCraft = useCallback((result: CraftResult) => {
    const slot = useNet.getState().craft?.slot ?? 0;
    setCraftBusy(true);
    net.send(C2S.SUBMIT_SPELL, {
      type: 'signature',
      chant: result.chant,
      description: result.description,
      gestureSamples: result.gestureSamples,
      slot,
    });
  }, []);

  /* --- leave ----------------------------------------------------------- */
  const leave = useCallback(() => {
    voiceBridge.stop();
    void net.leave();
    useNet.getState().reset();
    onLeave();
  }, [onLeave]);

  if (fatalError) {
    return (
      <div className="screen">
        <div className="panel" style={{ maxWidth: 480 }}>
          <h2 className="title" style={{ fontSize: 24 }}>연결 오류</h2>
          <p className="subtitle">{fatalError}</p>
          <button type="button" className="btn btn--primary" onClick={leave}>
            로비로
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'aborted') {
    return (
      <div className="screen">
        <div className="panel" style={{ maxWidth: 480 }} data-testid="match-aborted">
          <h2 className="title" style={{ fontSize: 24 }}>경기 중단</h2>
          <p className="subtitle">{abortReason || '상대와의 연결이 복구되지 않았습니다.'}</p>
          <p className="hint">
            재접속 유예 {TIMING.RECONNECT_GRACE_MS / 1000}초가 지나 경기가 무효 처리되었습니다.
          </p>
          <button type="button" className="btn btn--primary" onClick={leave}>
            로비로
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {combatActive || phase === 'roundEnd' ? <Hud /> : null}
      {phase === 'check' || phase === 'waiting' ? <ConnectionCheck /> : null}
      {phase === 'matchEnd' ? <MatchResult onLeave={leave} /> : null}

      {craft?.open ? (
        <div className="screen">
          <SpellCraft
            type="signature"
            title={`시그니처 주문 ${craft.slot + 1}`}
            deadline={craft.endsAt - net.clockOffset}
            submitting={craftBusy}
            intro={
              <p className="subtitle">
                {craft.round} 라운드에서 사용할 새 주문입니다. 공격, 회복, 함정, 이동, 방어, 강화, 약화 무엇이든 좋습니다.
                <br />
                확정하면 상대에게는 영창 이름과 대략적인 기능만 공개됩니다.
              </p>
            }
            onSubmit={submitCraft}
          />
        </div>
      ) : null}

      {/* The surface that acquires pointer lock. It cannot be dropped from later
          rounds: the browser only grants the lock on a gesture, so this is also
          how a player who pressed ESC — between rounds, mid-round, by accident —
          gets their mouse back. What it says is what changes. The full briefing
          is worth reading once; from the second round on the player has played a
          round already, and re-reading four lines to get their mouse back is
          noise, so it keeps only the line they might actually have forgotten. */}
      {combatActive && !locked ? (
        <div
          className="screen"
          style={{ background: 'rgba(4,6,14,0.35)', cursor: 'pointer' }}
          onClick={() => inputRef.current.requestLock()}
          data-testid="click-to-play"
        >
          <div className="panel" style={{ textAlign: 'center' }}>
            <div className="title" style={{ fontSize: 20 }}>클릭해서 조작 시작</div>
            {round <= 1 ? (
              <div className="hint">
                마우스 이동 = 시선 · <b>WASD</b> = 이동 · <b>아무 마우스 버튼이나 스페이스를 누른 채로</b> = 시전 · ESC = 마우스 해제
                <br />
                누른 채로 <b>영창을 외치고 문양을 그린 뒤 손을 뗍니다</b>. 시전 중 <b>Shift</b>로 획을 나눕니다.
                <br />
                날아오는 공격은 <b>막거나 비켜설 수 있습니다</b> — 다만 <b>시전 중에는 발이 묶입니다</b>.
                <br />
                주문을 고르지 않습니다 — 무엇을 외치고 무엇을 그렸는지로 결정됩니다. 패링도 패링 주문을 그렇게 외치면 됩니다.
              </div>
            ) : (
              <div className="hint">
                마우스 이동 = 시선 · <b>WASD</b> = 이동 · <b>누른 채로</b> = 시전 · ESC = 마우스 해제
              </div>
            )}
          </div>
        </div>
      ) : null}

      {showDebug ? <DebugOverlay /> : null}
    </>
  );
}

export const selectOpponentMeta = selectOpponent;
